import React, { useContext, useState, useEffect, useMemo, } from 'react'
import useHistoryCell from '../../tablecells/History'
import useOrderCell from '../../tablecells/Orders';
import useTransactionCell from '../../tablecells/Transaction';
import { ThemeContext } from '../../../context/ThemeContext'
import { Table, TableHead, TableBody, TableRow, TableCell, Paper, Menu, MenuItem, TextField, TableSortLabel, Box, Typography, Divider, Button } from '@mui/material';
import { ArrowRight } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check"
import '../../../App.scss'
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useTranslation } from "react-i18next";
import HistoryFooter from '../../footer/HistoryFooter';
import { useDispatch, useSelector } from "react-redux";
import { TableVirtuoso } from "react-virtuoso";
import { getCookies } from '../../../services/Cookies';
import { fetchAccountHistory } from '../../../store/thunks/orders';

export default function History() {
  const { t } = useTranslation()
  const { darkMode } = useContext(ThemeContext);
  const [contextMenu, setContextMenu] = useState(null);
  const [submenuAnchor, setSubmenuAnchor] = useState(null);
  const [submenuType, setSubmenuType] = useState(null);
  const { accountId: accountID } = useSelector(state => state.accountSummary)
  const { accountData } = useSelector((state) => state.accountSummary)
  const creation = accountData.creationDate
  const [filterPeriod, setFilterPeriod] = useState(() => {
    return localStorage.getItem("historyFilterPeriod") || "today";
  });
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [customDateRange, setCustomDateRange] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSymbol, setSelectedSymbol] = useState("");
  const [selectedType, setSelectedType] = useState("Trades");
  const orderCell = useOrderCell()
  const TransactionCell = useTransactionCell()
  const HistoryCell = useHistoryCell()
  const accountHistory = useSelector((state) => state.orderPosition.accountHistory);
  const [columnsToRender, setColumnsToRender] = useState(
    selectedType === "Trades" ? HistoryCell : selectedType === "Orders" ? orderCell : TransactionCell
  );
  const [activeRowId, setActiveRowId] = useState(null);
  const clientId = getCookies("clientId");
  const componentName = 'History'
  const storageKey = `hiddenColumns_${clientId}_${componentName}_${selectedType}`;
  const [uniqueSymbols, setUniqueSymbols] = useState([]);
  const [tempFromDate, setTempFromDate] = useState(fromDate); // used in the datepicker
  const [tempToDate, setTempToDate] = useState(toDate);
  const [isDateInvalid, setIsDateInvalid] = useState(false);
  const dispatch = useDispatch()
  const getInitialVisibleColumns = () => {
    const storedHiddenColumns = JSON.parse(localStorage.getItem(storageKey));
    const currentColumns = selectedType === "Trades" ? HistoryCell : selectedType === "Orders" ? orderCell : TransactionCell;

    if (storedHiddenColumns) {
      return currentColumns.reduce((acc, col) => ({
        ...acc,
        [col.title]: !storedHiddenColumns.includes(col.title),
      }), {});
    }

    return currentColumns.reduce((acc, col) => ({ ...acc, [col.title]: true }), {});
  };

  const [visibleColumns, setVisibleColumns] = useState(getInitialVisibleColumns);

  useEffect(() => {
    const newColumns = selectedType === "Trades" ? HistoryCell : selectedType === "Orders" ? orderCell : TransactionCell;
    setColumnsToRender(newColumns);

    const storedHiddenColumns = JSON.parse(localStorage.getItem(storageKey));
    if (storedHiddenColumns) {
      setVisibleColumns(
        newColumns.reduce((acc, col) => ({
          ...acc,
          [col.title]: !storedHiddenColumns.includes(col.title)
        }), {})
      );
    } else {
      setVisibleColumns(
        newColumns.reduce((acc, col) => ({ ...acc, [col.title]: true }), {})
      );
    }
  }, [selectedType]);

  useEffect(() => {
    const now = new Date();
    let from = null;
    let to = now;

    switch (filterPeriod) {
      case "today":
        from = dayjs().startOf('day');
        to = dayjs().endOf('day');
        break;
      case "lastWeek":
        from = dayjs().subtract(7, 'day').startOf('day');
        to = dayjs().endOf('day');
        break;
      case "lastMonth":
        from = dayjs().subtract(1, 'month').startOf('day');
        to = dayjs().endOf('day');
        break;
      case "Last3Month":
      case "Last3Months":
        from = dayjs().subtract(3, 'month').startOf('day');
        to = dayjs().endOf('day');
        break;
      case "Last6Month":
      case "Last6Months":
        from = dayjs().subtract(6, 'month').startOf('day');
        to = dayjs().endOf('day');
        break;
      case "LastYear":
        from = dayjs().subtract(1, 'year').startOf('day');
        to = dayjs().endOf('day');
        break;
      case "Custom":
        from = dayjs(fromDate).startOf('day');
        to = dayjs(toDate).endOf('day');
        break;
      default:
        from = creation ? dayjs(creation).startOf('day') : dayjs().startOf('day');
        to = dayjs().endOf('day');
    }

    if (accountID && from && to) {
      dispatch(fetchAccountHistory({
        accountId: accountID,
        from: from.format("YYYY-MM-DDTHH:mm:ss"),
        to: to.format("YYYY-MM-DDTHH:mm:ss")
      }));
    }
  }, [filterPeriod, fromDate, toDate, accountID, creation]);

  const getFilterRange = (period) => {
    const now = new Date();
    let _from = null;
    let _to = now.getTime();

    switch (period) {
      case "today":
        _from = new Date().setHours(0, 0, 0, 0);
        _to = new Date().setHours(23, 59, 59, 999);
        break;
      case "lastWeek":
        _from = dayjs().subtract(7, 'day').startOf('day').valueOf();
        break;
      case "lastMonth":
        _from = dayjs().subtract(1, 'month').startOf('day').valueOf();
        break;
      case "Last3Month":
      case "Last3Months":
        _from = dayjs().subtract(3, 'month').startOf('day').valueOf();
        break;
      case "Last6Month":
      case "Last6Months":
        _from = dayjs().subtract(6, 'month').startOf('day').valueOf();
        break;
      case "LastYear":
        _from = dayjs().subtract(1, 'year').startOf('day').valueOf();
        break;
      case "Custom":
        // _from = fromDate ? new Date(fromDate).getTime() : null;
        // _to = toDate ? new Date(toDate).getTime() : _to;
        _from = fromDate ? dayjs(fromDate).startOf('day').valueOf() : null;
        _to = toDate ? dayjs(toDate).endOf('day').valueOf() : _to;
        break;
      default:
        _from = creation ? new Date(creation).getTime() : null;
    }

    return { _from, _to };
  };
  const filteredHistory = useMemo(() => {
    if (!Array.isArray(accountHistory)) return [];

    const typeMap = {
      1: "Deposit",
      2: "Adjustment",
      3: "Credit In",
      4: "Withdrawal",
      5: "Credit Out",
    };
    return accountHistory.filter(row => {
      const matchesType =
        (selectedType === "Trades" && [10, 12].includes(row.type)) ||
        (selectedType === "Orders" && ![1, 2, 3, 4, 5, 10, 12].includes(row.type)) ||
        (selectedType === "Transaction" && [1, 2, 3, 4, 5].includes(row.type));

      const matchesSymbol = selectedSymbol
        ? (row.symbolData?.name || row.symbolName || "").trim().toLowerCase() === selectedSymbol.trim().toLowerCase()
        : true;

      const matchesSearch = !searchQuery ||
        Object.entries(row).some(([key, value]) => {
          if (key === 'type') {
            const label = typeMap[value];
            return label?.toLowerCase().includes(searchQuery.toLowerCase());
          }
          return value?.toString().toLowerCase().includes(searchQuery.toLowerCase());
        });

      return matchesType && matchesSymbol && matchesSearch;
    });
  }, [accountHistory, selectedType, selectedSymbol, searchQuery]);

  const historyByDate = useMemo(() => {
    if (!Array.isArray(accountHistory)) return [];

    const { _from, _to } = getFilterRange(filterPeriod);

    return accountHistory.filter(row => {
      // const rowDate = new Date(row.dateTime).getTime();
      const rowTime = row?.dateTime || row?.closeTime;
      const rowDate = dayjs(rowTime).valueOf();
      return (!_from || rowDate >= _from) && (!_to || rowDate <= _to);
    });
  }, [accountHistory, filterPeriod, fromDate, toDate, creation]);


  const handleContextMenu = (event, row) => {
    event.preventDefault();
    setContextMenu({ mouseX: event.clientX + 2, mouseY: event.clientY - 6, row });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSubmenuAnchor(null);
    setSubmenuType(null);
    setCustomDateRange(false)
  };

  const sortedFilteredHistory = useMemo(() => {
    if (!filteredHistory) return [];

    if (!sortConfig.key) return filteredHistory;

    return [...filteredHistory].sort((a, b) => {
      const valueA = a[sortConfig.key];
      const valueB = b[sortConfig.key];

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return sortConfig.direction === 'asc' ? valueA - valueB : valueB - valueA;
      }
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortConfig.direction === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      }
      return 0;
    });
  }, [filteredHistory, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  useEffect(() => {
    const filtered = historyByDate.filter(row => {
      return (
        (selectedType === "Trades" && [10, 12].includes(row.type)) ||
        (selectedType === "Orders" && ![1, 2, 3, 4, 5, 10, 12].includes(row.type)) ||
        (selectedType === "Transaction" && [1, 2, 3, 4, 5].includes(row.type))
      );
    });

    const symbols = [...new Set(
      filtered.map(trade => trade.symbolData?.name || trade.symbolName)
    )].filter(Boolean);

    setUniqueSymbols(symbols);
  }, [historyByDate, selectedType]);


  const totalProfitLoss = filteredHistory?.reduce((total, row) => total + (row?.profitLoss || 0), 0);

  const handleCloseFilterMenu = () => {
    setAnchorEl(null);
  };

  const handleSelectSymbol = (symbol) => {
    setSelectedSymbol(symbol === "All" ? "" : symbol); // Empty string means all symbols
    setAnchorEl(null);
  };

  const handleMenuItemClick = (type) => {
    setSelectedType(type);
    handleCloseContextMenu();
  };

  const toggleColumnVisibility = (title) => {
    setVisibleColumns((prev) => {
      const updatedColumns = { ...prev, [title]: !prev[title] };

      setTimeout(() => {
        localStorage.setItem(storageKey, JSON.stringify(
          Object.keys(updatedColumns).filter(col => !updatedColumns[col])
        ));
      }, 100);

      return updatedColumns;
    });
  };



  const resetAllColumns = () => {
    setVisibleColumns((prev) => {
      const resetColumns = columnsToRender.reduce(
        (acc, col) => ({ ...acc, [col.title]: true }),
        {}
      );

      setTimeout(() => {
        localStorage.removeItem(storageKey);
      }, 100);

      return resetColumns;
    });
  };


  const Row = useMemo(() => (index, row) => {
    return <>
      {columnsToRender
        .filter(col => visibleColumns?.[col.title])
        .map((col, colIndex) => {
          return (
            <>
              {visibleColumns?.[col.title] ? (
                <TableCell onClick={(e) => { setActiveRowId(row); }} onContextMenu={(event) => { handleContextMenu(event, row); setActiveRowId(row); }} key={colIndex} sx={{
                  fontSize: "0.8rem", border: "none", padding: "5px 5px",
                  position: "relative", overflow: "hidden",
                  color: col.dataIndex === "type" ? row.type === 1 ? "primary.main" : "secondary.main"
                    : col.dataIndex === "profitLoss" ? row.profitLoss < 0 ? "var(--red)" : row.profitLoss > 0 ? "var(--green)" : "inherit"
                      : col.dataIndex === "openTicket" ? row.status === "DONE" ? "green" : row.status === "CANCEL" ? "red" : "inherit"
                        : "inherit",
                }}
                >
                  {
                    col.dataIndex === "comment"
                      ? row.status ? row.status !== "CANCEL" ? `${row.status} - #${row.closeTicket ?? row.openTicket}` : row.status : row.comment || ""
                      // ? row.status ? row.closeTicket || row.openTicket ? `${row.status} - #${row.closeTicket ?? row.openTicket}` : row.status : row.comment || ""
                      : col.dataIndex === "openTicket" ? row.openTicket
                        : col.render ? col.render(row[col.dataIndex], row) : row[col.dataIndex]
                  }

                </TableCell>
              ) : null}
            </>
          );
        })}
    </>
  }, [columnsToRender, visibleColumns, darkMode, selectedType])

  const fixedHeaderContent = () => {
    return <TableRow sx={{ borderTop: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}` }}>
      {columnsToRender.map(
        (col, index) => visibleColumns[col.title] && (
          <TableCell onContextMenu={(event) => handleContextMenu(event, col)} key={index} sx={{ width: col?.width, fontSize: "0.85rem", fontWeight: 550, textTransform: "capitalize", p: 1, backgroundColor: darkMode ? "var(--background-color)" : "var(--white)", cursor: "pointer", borderBottom: "1px solid rgba(224, 224, 224, 0.3)", borderRight: `1px solid ${darkMode ? "#444" : "var(--borderLight)"}`, position: 'sticky', top: 0, zIndex: 2 }} onClick={() => handleSort(col.dataIndex)}>
            <TableSortLabel active={sortConfig.key === col.dataIndex} direction={sortConfig.direction}>{col.title}</TableSortLabel>
          </TableCell>
        )
      )}
    </TableRow>
  }

  const VirtuosoTableComponents = {
    Table: (props) => (
      <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
    ),
    TableHead: React.forwardRef((props, ref) => <TableHead {...props} ref={ref} />),
    TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
    TableRow: (props) => {
      const { item, ...rest } = props;
      const isActive = activeRowId === item;

      return (
        <TableRow {...rest} onClick={() => setActiveRowId(item)} sx={{ backgroundColor: isActive ? (darkMode ? 'var(--highlightDark)' : 'var(--highlightLight)') : (darkMode ? '#151924' : '#fff'), cursor: 'pointer', }} />
      );
    },
  };

  const periods = [
    { key: 'today', value: 'Today' },
    { key: 'lastWeek', value: 'Last Week' },
    { key: 'lastMonth', value: 'Last Month' },
    { key: 'Last3Month', value: 'Last 3 Month' },
    { key: 'Last6Month', value: 'Last 6 Month' },
    { key: 'allHistory', value: 'All History' }
  ];
  useEffect(() => {
    if (tempFromDate && tempToDate) {
      setIsDateInvalid(dayjs(tempToDate).isBefore(dayjs(tempFromDate), 'day'));
    }
  }, [tempFromDate, tempToDate]);

  useEffect(() => {
    localStorage.setItem("historyFilterPeriod", filterPeriod);
  }, [filterPeriod]);


  return (
    <>
      <Paper style={{ width: "100%", height: "calc(100% - 35px)", position: 'relative' }}>
        <Box onContextMenu={(event) => { handleContextMenu(event); }} sx={{ backgroundColor: darkMode ? "#151924" : "#fff", height: '100%' }}>
          <TableVirtuoso data={sortedFilteredHistory} itemContent={Row} fixedHeaderContent={fixedHeaderContent} components={VirtuosoTableComponents}> </TableVirtuoso>
        </Box>
      </Paper>
      <div style={{ height: 35 }}>
        <HistoryFooter handleMenuItemClick={handleMenuItemClick} setAnchorEl={setAnchorEl} totalProfitLoss={totalProfitLoss} selectedType={selectedType} sortedHistory={sortedFilteredHistory} searchQuery={searchQuery} setSearchQuery={setSearchQuery} anchorEl={anchorEl} handleCloseFilterMenu={handleCloseFilterMenu} uniqueSymbols={uniqueSymbols} handleSelectSymbol={handleSelectSymbol} selectedSymbol={selectedSymbol} />
      </div>

      <Menu sx={{ "& .MuiList-root": { padding: 0 }, }} open={contextMenu !== null} onClose={handleCloseContextMenu} anchorReference="anchorPosition" anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}>
        <div style={{ backgroundColor: darkMode ? "#262f3f" : "var(--white)", color: darkMode ? "var(--lightText)" : '#var(--darkText)' }}>
          <MenuItem sx={{ fontSize: 13.5, display: "flex", justifyContent: "space-between", alignItems: "center" }} onClick={() => handleMenuItemClick("Trades")}>{t('trades')}  {selectedType === "Trades" && <CheckIcon sx={{ fontSize: 18, color: "#4597ff" }} />}</MenuItem>
          <MenuItem sx={{ fontSize: 13.5, display: "flex", justifyContent: "space-between", alignItems: "center" }} onClick={() => handleMenuItemClick("Orders")}>{t('orders')}   {selectedType === "Orders" && <CheckIcon sx={{ fontSize: 18, color: "#4597ff" }} />}</MenuItem>
          <MenuItem sx={{ borderBottom: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, fontSize: 13.5, display: "flex", justifyContent: "space-between", alignItems: "center" }} onClick={() => handleMenuItemClick("Transaction")}>{t('transaction')}  {selectedType === "Transaction" && <CheckIcon sx={{ fontSize: 18, color: "#4597ff" }} />}</MenuItem>

          <MenuItem onClick={(e) => { setSubmenuAnchor(e.currentTarget); setSubmenuType("Period"); }} sx={{ fontSize: 13.5 }}>{t('period')} <ArrowRight /></MenuItem>
          <MenuItem onClick={(e) => { setSubmenuAnchor(e.currentTarget); setSubmenuType("Columns"); }} sx={{ fontSize: 13.5 }}>{t('columns')} <ArrowRight /></MenuItem>
        </div>
      </Menu>

      <Menu sx={{ "& .MuiList-root": { padding: 0 } }} open={Boolean(submenuAnchor)} anchorEl={submenuAnchor} onClose={handleCloseContextMenu} anchorOrigin={{ vertical: "top", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "left" }}>
        <div style={{ backgroundColor: darkMode ? "#262f3f" : "", color: darkMode ? "var(--lightText)" : '#var(--darkText)' }}>
          {submenuType === "Period" ? (
            customDateRange ? (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Typography variant='body2' textAlign='center' p={1}>{t('customRange')}</Typography>
                <Divider />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '18px', color: darkMode ? "var(--lightText)" : '#var(--darkText)' }}>
                  <DatePicker sx={{ padding: 0, mb: 2 }} label={t('fromDate')} value={dayjs(tempFromDate)} onChange={(newValue) => setTempFromDate(newValue)} minDate={dayjs(creation)} maxDate={dayjs()} />
                  <DatePicker sx={{ padding: 0 }} label={t('toDate')} value={dayjs(tempToDate)} onChange={(newValue) => setTempToDate(newValue)} minDate={dayjs(creation)} maxDate={dayjs()} />
                  <Box display="flex" justifyContent="end" alignItems='center' gap={2} mt={1}>
                    <Button size="small" variant='outlined' onClick={() => { setCustomDateRange(false); handleCloseContextMenu(); }}> {t("cancel")}</Button>
                    <Button disabled={isDateInvalid} size="small" variant="contained" onClick={() => { setFromDate(tempFromDate); setToDate(tempToDate); setFilterPeriod("Custom"); setCustomDateRange(false); handleCloseContextMenu(); }}> {t("ok")}</Button>
                  </Box>
                </div>
              </LocalizationProvider>
            ) : (
              <>
                {periods.map((period) => (
                  <MenuItem key={period.key} onClick={() => { setFilterPeriod(period.key); setSubmenuAnchor(null); handleCloseContextMenu(); }} sx={{ fontSize: 13.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {t(`${period.key}`)} {filterPeriod === period.key && <CheckIcon sx={{ fontSize: 18, color: "#4597ff" }} />}
                  </MenuItem>
                ))}
                <MenuItem key="Custom Range" onClick={() => { setFilterPeriod("Custom"); setCustomDateRange(true); }} sx={{ fontSize: 13.5 }}>{t('customRange')}</MenuItem>
              </>
            )
          ) : (
            <>
              {columnsToRender.filter(col => col.title !== `${t('symbol')}` && col.title !== `${t('P/L')}`).map((col, index) => (
                <MenuItem key={index} onClick={() => toggleColumnVisibility(col.title)} sx={{ fontSize: 13.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  {col.title} {visibleColumns[col.title] && <CheckIcon sx={{ fontSize: 18, color: "#4597ff" }} />}
                </MenuItem>
              ))}
              <MenuItem onClick={resetAllColumns} sx={{ fontSize: 13.5, display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, }}>{t("resetAll")}</MenuItem>
            </>
          )}
        </div>
      </Menu>
    </>)
}
